import { isEmpty } from '@sgme/fp';
import { decimalRules } from 'components/common-form/builder/form/node/field/SpotAndGreeksField/SpotAndGreeks.helper';
import { FormattedMessage, FormattedNumber } from 'components/shared/Intl';
import { UnderlyingTag } from 'components/underlying-tag';
import { Fragment, JSX } from 'react';
import styled from 'styled-components';

import { displayUnderlyingCode } from '../../utils';
import type { SpotAndGreeksFieldProps } from './SpotAndGreekField.connect';

const KeyValueWrapper = styled.span`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin: 2px 6px 2px 6px;
`;

export function HorizontalSpotAndGreeksFieldComponent({
  value: spotAndGreeks,
  id,
  defaultUnderlyingCodeType,
  tabIndex,
}: SpotAndGreeksFieldProps): JSX.Element | null {
  if (isEmpty(spotAndGreeks)) {
    return null;
  }

  return (
    <div data-e2e={`${id}-row`} className="d-flex">
      {spotAndGreeks.map((spotAndGreek, index) =>
        spotAndGreek.underlying !== undefined ? (
          <Fragment key={spotAndGreek.underlying.id}>
            {spotAndGreek.isForex ? (
              <>
                <KeyValueWrapper className="ms-0">
                  <span className="pe-1">Spot</span>
                  <UnderlyingTag
                    label={displayUnderlyingCode(defaultUnderlyingCodeType, spotAndGreek.underlying)}
                    value={spotAndGreek.underlying}
                    className="align-self-start mb-2"
                    isReadOnly
                  />
                  <span className="fs-6" data-e2e="SpotAndGreeks-spot">
                    <FormattedNumber
                      value={spotAndGreek.spot}
                      minimumFractionDigits={decimalRules.Forex.spot.minimumFractionDigits}
                      maximumFractionDigits={decimalRules.Forex.spot.maximumFractionDigits}
                      tabIndex={tabIndex}
                    />
                  </span>
                </KeyValueWrapper>
                {spotAndGreek.fxDelta !== undefined && (
                  <KeyValueWrapper className="ms-0">
                    <span className="fs-6 fw-normal text-nowrap me-1">
                      {spotAndGreek.fxDelta.reference?.replace('(%)', '')}
                    </span>
                    <span className="fs-6" data-e2e="SpotAndGreeks-fxDelta">
                      <FormattedNumber
                        value={spotAndGreek.fxDelta.value}
                        minimumFractionDigits={decimalRules.Forex.fxDelta.minimumFractionDigits}
                        maximumFractionDigits={decimalRules.Forex.fxDelta.maximumFractionDigits}
                        tabIndex={tabIndex}
                      />
                      %
                    </span>
                  </KeyValueWrapper>
                )}
                {spotAndGreek.fxVega !== undefined && (
                  <KeyValueWrapper className="ms-0">
                    <span className="fs-6 font-weight-normal text-nowrap me-1">
                      {spotAndGreek.fxVega.reference?.replace('(%)', '')}
                    </span>
                    <span className="fs-6" data-e2e="SpotAndGreeks-fxVega">
                      <FormattedNumber
                        value={spotAndGreek.fxVega.value}
                        minimumFractionDigits={decimalRules.Forex.fxVega.minimumFractionDigits}
                        maximumFractionDigits={decimalRules.Forex.fxVega.maximumFractionDigits}
                        tabIndex={tabIndex}
                      />
                      %
                    </span>
                  </KeyValueWrapper>
                )}
              </>
            ) : (
              <>
                <UnderlyingTag
                  label={displayUnderlyingCode(defaultUnderlyingCodeType, spotAndGreek.underlying)}
                  value={spotAndGreek.underlying}
                  className="align-self-start mb-2"
                  isReadOnly
                />
                {spotAndGreek.spot !== undefined && (
                  <KeyValueWrapper>
                    <span className="fs-6 fw-normal">
                      <FormattedMessage id="form.SpotAndGreeks.Spot" />
                    </span>
                    <span className="fs-6 ms-1" data-e2e="SpotAndGreeks-spot">
                      <FormattedNumber
                        value={spotAndGreek.spot}
                        minimumFractionDigits={decimalRules.Equity.spot.minimumFractionDigits}
                        maximumFractionDigits={decimalRules.Equity.spot.maximumFractionDigits}
                        tabIndex={tabIndex}
                      />
                    </span>
                  </KeyValueWrapper>
                )}
                {spotAndGreek.deltaPct !== undefined && (
                  <KeyValueWrapper>
                    <span className="fs-6 fw-normal">
                      <FormattedMessage id="form.SpotAndGreeks.Delta" />
                    </span>
                    <span className="fs-6 ms-1" data-e2e="SpotAndGreeks-delta">
                      <FormattedNumber
                        value={spotAndGreek.deltaPct}
                        minimumFractionDigits={decimalRules.Equity.deltaPct.minimumFractionDigits}
                        maximumFractionDigits={decimalRules.Equity.deltaPct.maximumFractionDigits}
                        tabIndex={tabIndex}
                      />
                      %
                    </span>
                  </KeyValueWrapper>
                )}
              </>
            )}

            {spotAndGreeks.length - 1 !== index && ' '}
          </Fragment>
        ) : null,
      )}
    </div>
  );
}
